import dayjs from 'dayjs';
import React from 'react';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { Grid } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { Autocomplete, Button, TextField } from 'shared';
import { activityFiltersStyles } from 'components/Inventory/style/style';
import { types } from 'components/Inventory/utils/forReviewTransactionTypes';
import { parseExcludedFilters } from 'components/Inventory/utils/parseFilters';
import { setFilters, clearFilters } from 'store/slices/inventoryFilters.slice';
import { excludedSchema, excludedInitialValues } from 'components/Inventory/utils/excludedSchema';

const ExcludedFilters = ({
    users,
    banks,
    selected,
    handleRevertAll,
    downloadLoading,
    revertAllLoading,
    handleFiltersSubmit,
    handleDownloadExcluded,
}) => {
    const dispatch = useDispatch();
    const classes = activityFiltersStyles();
    const filters = useSelector((state) => state.inventoryFilter);

    const handleClearClick = (handleReset) => {
        handleReset();
        dispatch(clearFilters());
        handleFiltersSubmit({});
    };

    const handleFormSubmit = (values) => {
        dispatch(setFilters(values));
        const filters = parseExcludedFilters(values);
        handleFiltersSubmit(filters);
    };

    return (
        <Formik
            enableReinitialize
            onSubmit={handleFormSubmit}
            validationSchema={excludedSchema}
            initialValues={{ ...excludedInitialValues, ...filters }}
        >
            {({ values, handleReset, handleChange, setFieldValue }) => {
                return (
                    <Form>
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6} lg={3}>
                                <Autocomplete
                                    options={banks}
                                    labelKey="label"
                                    valueKey="value"
                                    placeholder="Bank Account"
                                    name="bankAccount"
                                    inlineLabel="Bank Account"
                                    value={banks.find((bank) => bank.value == values.bankAccount)}
                                    onChange={(e, value) =>
                                        setFieldValue('bankAccount', value.value)
                                    }
                                    isSearchField={false}
                                    disableClearable
                                    customStyle={classes.dropdown}
                                    showTotal={true}
                                />
                            </Grid>

                            <Grid item xs={12} />

                            <Grid item xs={4} md={2} lg={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={values.startDate && dayjs(values.startDate)}
                                        onChange={(date) => setFieldValue('startDate', date)}
                                        label="Start Date"
                                        placeholder="Start Date"
                                        format="MM-DD-YYYY"
                                        sx={classes.datePicker}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={4} md={2} lg={2}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        value={values.endDate && dayjs(values.endDate)}
                                        onChange={(date) => setFieldValue('endDate', date)}
                                        label="End Date"
                                        placeholder="End Date"
                                        format="MM-DD-YYYY"
                                        sx={classes.datePicker}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={4} md={2} lg={2}>
                                <TextField
                                    fullWidth
                                    name="amount"
                                    inlineLabel="Amount"
                                    value={values.amount}
                                    placeholder="Search by amount"
                                    customStyle={classes.inputField}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (/^-?\d*\.?\d*$/.test(val)) {
                                            handleChange(e);
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid item xs={4} md={2} lg={2}>
                                <TextField
                                    fullWidth
                                    name="description"
                                    inlineLabel="Description"
                                    value={values.description}
                                    placeholder="Search by description"
                                    onChange={handleChange}
                                    customStyle={classes.inputField}
                                />
                            </Grid>

                            <Grid item xs={4} md={2} lg={2}>
                                <Autocomplete
                                    options={users}
                                    labelKey="label"
                                    valueKey="value"
                                    placeholder="User"
                                    name="excludedBy"
                                    inlineLabel="Excluded By"
                                    value={values.excludedBy}
                                    onChange={(e, value) => setFieldValue('excludedBy', value)}
                                    isSearchField={false}
                                    disableClearable
                                    customStyle={classes.dropdown}
                                    showTotal={true}
                                />
                            </Grid>

                            <Grid item xs={4} md={2} lg={2}>
                                <Autocomplete
                                    options={types.length > 1 ? types.slice(0, -1) : []}
                                    labelKey="label"
                                    valueKey="value"
                                    placeholder="Type"
                                    name="type"
                                    inlineLabel="Type"
                                    value={values.type}
                                    onChange={(e, value) => setFieldValue('type', value)}
                                    isSearchField={false}
                                    disableClearable
                                    customStyle={classes.dropdown}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Grid item>
                                <Button
                                    text="Download CSV"
                                    loading={downloadLoading}
                                    onClick={handleDownloadExcluded}
                                />
                            </Grid>

                            <Grid item marginTop="10px" marginBottom="10px">
                                {selected && selected.length > 0 && (
                                    <Button
                                        text="Revert All"
                                        loading={revertAllLoading}
                                        onClick={handleRevertAll}
                                        customStyles={classes.revertAllButton}
                                    />
                                )}

                                <Button
                                    text="Clear"
                                    onClick={() => handleClearClick(handleReset)}
                                    customStyles={classes.clearButton}
                                />
                                <Button text="Apply" type="submit" />
                            </Grid>
                        </Grid>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ExcludedFilters;
